import { ReactNode, useMemo } from 'react'

import { useBalanceLabels } from '@features/balanceLedger'
import { isSortableLedgerColumn, LedgerEntryColumn } from '@features/balanceLedger/enums/ledgerEntryColumn'

import { LEDGER_ENTRIES_COLUMN_WIDTHS } from '../constants/ledgerEntriesColumnWidths'
import { amountCell, balanceCell, dateCell, journalCell } from '../constants/LedgerEntriesTableCells'
import { ColumnHeaderWithUnit } from '../elements/SuppliersBalanceTable/elements/ColumnHeaderWithUnit'
import { TruncatedTextCell } from '../elements/TruncatedTextCell'
import type { LedgerEntry } from '../types/LedgerEntry'

export type LedgerEntryColumnAlign = 'start' | 'end'

export interface LedgerEntryColumnDef {
  id: LedgerEntryColumn
  header: ReactNode
  sortable: boolean
  align?: LedgerEntryColumnAlign
  width?: string
  Cell: (entry: LedgerEntry, isBold?: boolean) => ReactNode
}

export const useLedgerEntriesTableColumns = (currencyId?: string): LedgerEntryColumnDef[] => {
  const { bt } = useBalanceLabels()
  const currencyUnit = currencyId ? `(${currencyId})` : ''

  return useMemo(
    (): LedgerEntryColumnDef[] => [
      {
        id: LedgerEntryColumn.Date,
        header: bt('detail_view.column.date'),
        sortable: isSortableLedgerColumn(LedgerEntryColumn.Date),
        width: LEDGER_ENTRIES_COLUMN_WIDTHS.date,
        Cell: dateCell,
      },
      {
        id: LedgerEntryColumn.Journal,
        header: bt('detail_view.column.journal'),
        sortable: isSortableLedgerColumn(LedgerEntryColumn.Journal),
        width: LEDGER_ENTRIES_COLUMN_WIDTHS.journal,
        Cell: journalCell,
      },
      {
        id: LedgerEntryColumn.Description,
        header: bt('detail_view.column.description'),
        sortable: isSortableLedgerColumn(LedgerEntryColumn.Description),
        Cell: (entry) => <TruncatedTextCell value={entry.description} />,
      },
      {
        id: LedgerEntryColumn.DocumentNumber,
        header: bt('detail_view.column.document_number'),
        sortable: isSortableLedgerColumn(LedgerEntryColumn.DocumentNumber),
        width: LEDGER_ENTRIES_COLUMN_WIDTHS.documentNumber,
        Cell: (entry) => <TruncatedTextCell value={entry.documentNumber} />,
      },
      {
        id: LedgerEntryColumn.Debit,
        header: <ColumnHeaderWithUnit label={bt('detail_view.column.debit')} unit={currencyUnit} />,
        sortable: isSortableLedgerColumn(LedgerEntryColumn.Debit),
        align: 'end',
        width: LEDGER_ENTRIES_COLUMN_WIDTHS.amount,
        Cell: (entry, isBold) => amountCell(entry.debit, isBold),
      },
      {
        id: LedgerEntryColumn.Credit,
        header: <ColumnHeaderWithUnit label={bt('detail_view.column.credit')} unit={currencyUnit} />,
        sortable: isSortableLedgerColumn(LedgerEntryColumn.Credit),
        align: 'end',
        width: LEDGER_ENTRIES_COLUMN_WIDTHS.amount,
        Cell: (entry, isBold) => amountCell(entry.credit, isBold),
      },
      {
        id: LedgerEntryColumn.Balance,
        header: <ColumnHeaderWithUnit label={bt('detail_view.column.balance')} unit={currencyUnit} />,
        sortable: isSortableLedgerColumn(LedgerEntryColumn.Balance),
        align: 'end',
        width: LEDGER_ENTRIES_COLUMN_WIDTHS.amount,
        Cell: balanceCell,
      },
    ],
    [bt, currencyUnit],
  )
}
